import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import './css/PieChartCard.css';

const PieChartCard = ({
  data,
  title,
  dataKey,
  nameKey,
  colors = ['#3a7bd5', '#00b894', '#f7b731', '#ff6b6b', '#6c5ce7']
}) => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="pie-chart-card">
      <h3>{title}</h3>
      <div className="pie-chart-card__chart">
        <ResponsiveContainer width="100%" height={250}>
          <PieChart margin={isMobile ? { bottom: 0 } : { right: 150 }}>
            <Pie
              data={data}
              cx={isMobile ? '50%' : '75%'}
              cy="50%"
              outerRadius={110}
              dataKey={dataKey}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip />
            {!isMobile && (
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="middle"
                wrapperStyle={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      </div>
      {isMobile && (
        <div className="pie-chart-card__legend" role="list">
          {data.map((entry, index) => {
            const label = entry?.[nameKey] ?? `Segment ${index + 1}`;
            const value = entry?.percentage ?? entry?.[dataKey];
            const valueDisplay = typeof entry?.percentage === 'number'
              ? `${entry.percentage.toFixed(1)}%`
              : typeof value === 'number'
                ? value.toLocaleString()
                : value;

            return (
              <div className="pie-chart-card__legend-item" role="listitem" key={`${label}-${index}`}>
                <div className="pie-chart-card__legend-meta">
                  <span
                    className="pie-chart-card__legend-swatch"
                    style={{ backgroundColor: colors[index % colors.length] }}
                  />
                  <span className="pie-chart-card__legend-label">{label}</span>
                </div>
                {valueDisplay && (
                  <span className="pie-chart-card__legend-value">{valueDisplay}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

PieChartCard.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      percentage: PropTypes.number,
    })
  ).isRequired,
  title: PropTypes.string.isRequired,
  dataKey: PropTypes.string.isRequired,
  nameKey: PropTypes.string.isRequired,
  colors: PropTypes.arrayOf(PropTypes.string),
};

export default PieChartCard;
